/**
 * NEXUS V3 - Learning Center
 * 
 * Dedicated interface for Learn Agent, Knowledge Bank management,
 * feedback collection, and learning analytics.
 */

import React, { useState, useEffect } from 'react';
import { Brain, BookOpen, TrendingUp, Users, Settings, Target, BarChart3, Lightbulb } from 'lucide-react';
import { getApiBaseUrl, getApiRootUrl } from '@/services/api';

interface LearningCenterProps { }

interface LearnedPattern {
  change_type: string;
  escalation_likelihood: number;
  confidence: number;
  feedback_count: number;
  learning_maturity: string;
  evidence: string;
}

interface KnowledgeBankSummary {
  total_patterns: number;
  patterns_loaded: string[];
  pattern_examples: string[];
}

const LearningCenter: React.FC<LearningCenterProps> = () => {
  const [learnedPatterns, setLearnedPatterns] = useState<Record<string, LearnedPattern>>({});
  const [knowledgeBank, setKnowledgeBank] = useState<KnowledgeBankSummary | null>(null);
  const [feedbackStats, setFeedbackStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLearningData();
  }, []);

  const loadLearningData = async () => {
    try {
      const API_BASE = getApiBaseUrl();

      // Load learned patterns
      const patternsResponse = await fetch(`${API_BASE}/feedback/patterns`);
      if (patternsResponse.ok) {
        const patternsData = await patternsResponse.json();
        setLearnedPatterns(patternsData.data || {});
      }

      // Load feedback statistics
      const statsResponse = await fetch(`${API_BASE}/feedback/stats`);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setFeedbackStats(statsData.data || {});
      }

      // Load Knowledge Bank data
      const kbResponse = await fetch(`${getApiRootUrl()}/servicenow/knowledge-bank`);
      if (kbResponse.ok) {
        const kbData = await kbResponse.json();
        setKnowledgeBank(kbData);
      }

    } catch (error) {
      console.error('Failed to load learning data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getConfidenceColor = (likelihood: number) => {
    if (likelihood > 0.7) return 'text-red-600 bg-red-100';
    if (likelihood > 0.4) return 'text-yellow-600 bg-yellow-100';
    return 'text-green-600 bg-green-100';
  };

  const getMaturityBadge = (maturity: string) => {
    return maturity === 'mature'
      ? 'bg-green-100 text-green-800'
      : 'bg-blue-100 text-blue-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Brain className="mx-auto h-12 w-12 text-blue-500 animate-pulse" />
          <p className="mt-2 text-gray-600">Loading Learning Center...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 rounded-lg shadow-lg p-6 text-white">
        <div className="flex items-center gap-3 mb-4">
          <Brain className="h-10 w-10" />
          <div>
            <h1 className="text-3xl font-bold">Learning Center</h1>
            <p className="text-blue-100">AI-Powered Pattern Learning & Knowledge Management</p>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              <span className="text-sm font-medium">Knowledge Bank</span>
            </div>
            <div className="text-2xl font-bold mt-1">{knowledgeBank?.total_patterns || 0}</div>
            <div className="text-xs text-blue-200">Enterprise Patterns</div>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              <span className="text-sm font-medium">Learned Patterns</span>
            </div>
            <div className="text-2xl font-bold mt-1">{Object.keys(learnedPatterns).length}</div>
            <div className="text-xs text-blue-200">Active Rules</div>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              <span className="text-sm font-medium">User Feedback</span>
            </div>
            <div className="text-2xl font-bold mt-1">{feedbackStats?.total_feedback_received || 0}</div>
            <div className="text-xs text-blue-200">Interactions</div>
          </div>
          <div className="bg-white bg-opacity-10 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              <span className="text-sm font-medium">System Maturity</span>
            </div>
            <div className="text-2xl font-bold mt-1">{feedbackStats?.system_learning_maturity || 'Initial'}</div>
            <div className="text-xs text-blue-200">Learning Phase</div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Knowledge Bank Overview */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <BookOpen className="h-6 w-6 text-blue-600" />
            <h2 className="text-xl font-semibold">Knowledge Bank</h2>
          </div>

          {knowledgeBank && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded">
                  <div className="text-2xl font-bold text-blue-600">{knowledgeBank.total_patterns}</div>
                  <div className="text-sm text-gray-600">Total Patterns</div>
                </div>
                <div className="bg-green-50 p-3 rounded">
                  <div className="text-2xl font-bold text-green-600">{knowledgeBank.patterns_loaded.length}</div>
                  <div className="text-sm text-gray-600">Active Patterns</div>
                </div>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">Key Enterprise Patterns:</h3>
                <ul className="space-y-2">
                  {knowledgeBank.pattern_examples.map((pattern, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      {pattern}
                    </li>
                  ))}
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">🏗️ DT Architecture Patterns:</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• GK POS → Mulesoft → Hybris → SAP ECC flow</li>
                  <li>• Cross-system price reconciliation</li>
                  <li>• Integration health monitoring</li>
                  <li>• Manual correction processes</li>
                </ul>
              </div>

              <div>
                <h3 className="font-medium text-gray-900 mb-2">🎯 Authoritative Source Rules:</h3>
                <div className="bg-blue-50 p-3 rounded space-y-1 text-sm">
                  <div className="font-medium text-blue-900 mb-2">Execute Agent uses these rules to determine correct values:</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">💰</span>
                      <span><strong>Prices:</strong> GKPOS (primary)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">📦</span>
                      <span><strong>Line Items:</strong> GKPOS</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">🧾</span>
                      <span><strong>Taxes:</strong> SAP ECC (authority)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">💳</span>
                      <span><strong>Payments:</strong> SAP ECC</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">👤</span>
                      <span><strong>Customer:</strong> GKPOS (entry)</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">📊</span>
                      <span><strong>Inventory:</strong> SAP ECC</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">🔄</span>
                      <span><strong>Status (Pre):</strong> GKPOS/Hybris</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-blue-600">✅</span>
                      <span><strong>Status (Post):</strong> SAP ECC</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Learned Escalation Patterns */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="h-6 w-6 text-green-600" />
            <h2 className="text-xl font-semibold">Learned Escalation Patterns</h2>
          </div>

          <div className="space-y-3">
            {Object.entries(learnedPatterns).map(([changeType, pattern]) => (
              <div key={changeType} className="border rounded-lg p-3">
                <div className="flex justify-between items-start mb-2">
                  <div className="font-medium text-gray-900">{changeType.replace('_', ' ')}</div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${getMaturityBadge(pattern.learning_maturity)}`}>
                    {pattern.learning_maturity}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Escalation Likelihood:</span>
                    <div className={`mt-1 px-2 py-1 rounded text-center font-medium ${getConfidenceColor(pattern.escalation_likelihood)}`}>
                      {(pattern.escalation_likelihood * 100).toFixed(0)}%
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-600">Confidence:</span>
                    <div className="mt-1 text-gray-900 font-medium">
                      {(pattern.confidence * 100).toFixed(0)}%
                    </div>
                  </div>
                </div>

                <div className="mt-2 text-xs text-gray-600">
                  {pattern.feedback_count} feedback instances • {pattern.evidence}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Learning Analytics */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 className="h-6 w-6 text-purple-600" />
            <h2 className="text-xl font-semibold">Learning Analytics</h2>
          </div>

          {feedbackStats && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-green-50 p-3 rounded">
                  <div className="text-2xl font-bold text-green-600">{feedbackStats.mature_patterns}</div>
                  <div className="text-sm text-gray-600">Mature Patterns</div>
                  <div className="text-xs text-gray-500 mt-1">High confidence rules</div>
                </div>
                <div className="bg-blue-50 p-3 rounded">
                  <div className="text-2xl font-bold text-blue-600">{feedbackStats.learning_patterns}</div>
                  <div className="text-sm text-gray-600">Learning Patterns</div>
                  <div className="text-xs text-gray-500 mt-1">Still adapting</div>
                </div>
              </div>

              <div className="bg-purple-50 p-4 rounded">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="h-5 w-5 text-purple-600" />
                  <span className="font-medium">System Learning Progress</span>
                </div>
                <div className="text-sm text-gray-700">
                  Average Confidence: <span className="font-medium">{(feedbackStats.average_confidence * 100).toFixed(1)}%</span>
                </div>
                <div className="text-sm text-gray-700 mt-1">
                  Learning Phase: <span className="font-medium capitalize">{feedbackStats.system_learning_maturity}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Learning Opportunities */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb className="h-6 w-6 text-yellow-600" />
            <h2 className="text-xl font-semibold">Learning Opportunities</h2>
          </div>

          <div className="space-y-3">
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-3">
              <h3 className="font-medium text-yellow-800">Immediate Actions</h3>
              <ul className="mt-2 space-y-1 text-sm text-yellow-700">
                <li>• Collect more feedback on customer info changes</li>
                <li>• Refine financial change detection patterns</li>
                <li>• Expand DT architecture integration patterns</li>
              </ul>
            </div>

            <div className="bg-blue-50 border-l-4 border-blue-400 p-3">
              <h3 className="font-medium text-blue-800">Future Enhancements</h3>
              <ul className="mt-2 space-y-1 text-sm text-blue-700">
                <li>• Implement GKPOS → Hybris → SAP flow monitoring</li>
                <li>• Add Mulesoft integration health patterns</li>
                <li>• Enhance price reconciliation algorithms</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LearningCenter;
